/**
 * Extract a brand-oriented color palette from an image using canvas pixel sampling.
 */

function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const h = Math.max(0, Math.min(255, Math.round(x))).toString(16);
        return h.length === 1 ? "0" + h : h;
      })
      .join("")
  );
}

function parseHex(hex) {
  const h = hex.replace("#", "");
  if (h.length !== 6) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function relativeLuminance(r, g, b) {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function saturation(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) return 0;
  return (max - min) / max;
}

function darkenHex(hex, factor = 0.72) {
  const { r, g, b } = parseHex(hex);
  return rgbToHex(r * factor, g * factor, b * factor);
}

function lightenHex(hex, mix = 0.35) {
  const { r, g, b } = parseHex(hex);
  return rgbToHex(
    r + (255 - r) * mix,
    g + (255 - g) * mix,
    b + (255 - b) * mix
  );
}

function veryLightBackground(hex) {
  const { r, g, b } = parseHex(hex);
  return rgbToHex(
    r + (255 - r) * 0.94,
    g + (255 - g) * 0.94,
    b + (255 - b) * 0.94
  );
}

function pastelAccent(hex) {
  const { r, g, b } = parseHex(hex);
  return rgbToHex(
    r + (255 - r) * 0.85,
    g + (255 - g) * 0.75,
    b + (255 - b) * 0.7
  );
}

/**
 * Load image into HTMLImageElement. Uses crossOrigin for remote URLs (may fail if CORS blocked).
 */
function loadImage(src, crossOrigin = "anonymous") {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const skipCors = src.startsWith("blob:") || src.startsWith("data:");
    if (crossOrigin && !skipCors) {
      img.crossOrigin = crossOrigin;
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

/**
 * Sample image and return ranked hex colors (most "brand relevant" first).
 */
function sampleImageToColors(img, sampleSize = 80) {
  const canvas = document.createElement("canvas");
  const w = sampleSize;
  const h = Math.round((img.naturalHeight / img.naturalWidth) * sampleSize) || sampleSize;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  const buckets = new Map();
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 120) continue;
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];
    const lum = relativeLuminance(r, g, b);
    // Skip near-white and near-black
    if (lum > 0.92) continue;
    if (lum < 0.06) continue;
    const qr = (r >> 3) << 3;
    const qg = (g >> 3) << 3;
    const qb = (b >> 3) << 3;
    const key = `${qr},${qg},${qb}`;
    const sat = saturation(qr, qg, qb);
    const prev = buckets.get(key) || { count: 0, satSum: 0 };
    prev.count += 1;
    prev.satSum += sat;
    buckets.set(key, prev);
  }

  const scored = [...buckets.entries()].map(([key, v]) => {
    const [r, g, b] = key.split(",").map(Number);
    const avgSat = v.satSum / v.count;
    const score = v.count * (1 + avgSat * 2);
    return { r, g, b, score, hex: rgbToHex(r, g, b) };
  });

  scored.sort((a, b) => b.score - a.score);

  const unique = [];
  const minDist = 45;
  for (const c of scored) {
    if (unique.length >= 6) break;
    const tooClose = unique.some((u) => {
      const d = Math.sqrt(
        Math.pow(c.r - u.r, 2) + Math.pow(c.g - u.g, 2) + Math.pow(c.b - u.b, 2)
      );
      return d < minDist;
    });
    if (!tooClose) unique.push(c);
  }

  return unique.map((c) => c.hex);
}

/**
 * Build brand field map from extracted swatches.
 */
export function swatchesToBrandColors(swatches) {
  if (!swatches.length) {
    throw new Error("No colors could be extracted from this image.");
  }
  const primary = swatches[0];
  const secondary = swatches[1] || darkenHex(primary, 0.55);
  const accent = swatches[2] ? pastelAccent(swatches[2]) : pastelAccent(primary);

  return {
    color_primary: primary,
    color_primary_dark: darkenHex(primary, 0.68),
    color_primary_light: lightenHex(primary, 0.28),
    color_secondary: secondary,
    color_accent: accent,
    color_background: veryLightBackground(primary),
    color_surface: "#FFFFFF",
    color_text_primary: "#1E1E1E",
    color_text_secondary: "#838383",
  };
}

/**
 * Extract palette from a remote URL or same-origin URL. Fails if canvas is tainted (CORS).
 */
export async function extractPaletteFromUrl(url) {
  const img = await loadImage(url, "anonymous");
  const swatches = sampleImageToColors(img);
  return swatchesToBrandColors(swatches);
}

/**
 * Extract palette from a local File (no CORS issues).
 */
export async function extractPaletteFromFile(file) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl, false);
    const swatches = sampleImageToColors(img);
    return swatchesToBrandColors(swatches);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
